<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6">
      <!-- Header with create button -->
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-2xl font-bold text-gray-800">Registri Personalizzati</h1>
        <button 
          v-if="$page.props.isAdminOrSuperAdmin"
          @click="openCreateModal" 
          class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md shadow-md flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Nuovo Registro
        </button>
      </div>

      <!-- Customer Search (if enabled) -->
      <div v-if="isCustomerBasedSchemaEnabledLocal" class="mb-6">
        <div class="relative">
          <label class="inline-block text-gray-700 text-sm font-medium mb-2">Ricerca per Cliente</label>
          <v-select
            v-model="selectedCustomer"
            class="w-full bg-white rounded-md focus:ring focus:ring-blue-200 focus:border-blue-500 p-2" 
            :options="allCustomers"
            label="companyName"
            placeholder="Digita nome o codice cliente..."
            :clearable="true"
            :filter-by="(option, label, search) => {
              const q = search.toLowerCase();
              return (option.companyName || '').toLowerCase().includes(q)
                  || (option.custSupp || '').toLowerCase().includes(q);
            }"
          >
            <template #option="option">
              <span class="font-medium">{{ option.companyName }}</span>
              <span class="text-gray-400 text-sm ml-2">({{ option.custSupp }})</span>
            </template>
            <template #selected-option="option">
              <span>{{ option.companyName }} <span class="text-gray-400 text-sm">({{ option.custSupp }})</span></span>
            </template>
            <template #no-options>
              Nessun cliente trovato.
            </template>
          </v-select>

          <!-- Active Filter Badge -->
          <div v-if="activeCustomerFilter" class="mt-2 flex items-center gap-2">
            <span class="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
              Filtro: {{ activeCustomerFilter }}
            </span>
            <button 
              @click="clearCustomerFilter"
              class="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Cancella filtro
            </button>
          </div>
        </div>
      </div>

      <!-- Server Table (always shown) -->
      <PageServerTable
        :headers="headers"
        :items="items"
        :items-per-page.sync="ipp"
        :items-per-page-options="ippo"
        :sort-by="sortby"
        :items-length="count"
        :reload-fn="reload"
        :item-value="'id'"
        :page="page"
        show-current-page
        @click:row="openRegistry"
      >
        <!-- Name column -->
        <template #item.name="{ item }">
          <span class="font-medium">{{ item.name }}</span>
        </template>

        <!-- Description column -->
        <template #item.description="{ item }">
          <span class="text-gray-600 text-sm">{{ item.description }}</span>
        </template>

        <!-- Actions column -->
        <template #item.actions="{ item }">
          <span v-if="$page.props.isAdminOrSuperAdmin" class="flex grow-1 justify-end">
            <button @click.stop="openEditModal(item)" class="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded-md mr-2" title="Modifica">
              <v-icon name="bi-pencil" scale="1.2" />
            </button>
            <button @click.stop="confirmDelete(item)" class="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-md" title="Elimina">
              <v-icon name="fa-trash" scale="1.2" />
            </button>
          </span>
        </template>
      </PageServerTable>
    </div>

    <!-- Create/Edit Modal -->
    <div 
      v-if="showModal" 
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      @click="closeModal"
    >
      <div class="bg-white p-6 rounded-lg w-full max-w-md" @click.stop>
        <h2 class="text-xl font-semibold mb-4">
          {{ isEditing ? 'Modifica Registro' : 'Nuovo Registro' }}
        </h2>
        
        <form @submit.prevent="submitForm">
          <!-- General Error Messages -->
          <div v-if="Object.keys(form.errors).length > 0" class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            <div v-for="(error, key) in form.errors" :key="key">
              {{ error }}
            </div>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Nome *</label>
            <input 
              v-model="form.name" 
              type="text" 
              class="p93__input w-full"
              placeholder="Es. Registro Impianti 2024"
              required
            />
            <div v-if="form.errors.name" class="text-red-600 text-sm mt-1">{{ form.errors.name }}</div>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-medium mb-2">Descrizione</label>
            <textarea 
              v-model="form.description" 
              class="p93__input w-full"
              rows="3"
              placeholder="Descrizione opzionale del registro..."
            ></textarea>
            <div v-if="form.errors.description" class="text-red-600 text-sm mt-1">{{ form.errors.description }}</div>
          </div>

          <div class="flex justify-end gap-2">
            <button 
              type="button" 
              @click="closeModal"
              class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
            >
              Annulla
            </button>
            <button 
              type="submit" 
              :disabled="form.processing"
              class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {{ isEditing ? 'Aggiorna' : 'Crea' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteModal" 
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      @click="closeDeleteModal"
    >
      <div class="bg-white p-6 rounded-lg w-full max-w-md" @click.stop>
        <h2 class="text-xl font-semibold mb-4 text-red-600">Conferma Eliminazione</h2>
        <p class="text-gray-700 mb-4">
          Sei sicuro di voler eliminare il registro "<strong>{{ registryToDelete?.name }}</strong>"? 
          Questa azione eliminerà anche tutti i record associati.
        </p>
        
        <div class="flex justify-end gap-2">
          <button 
            type="button" 
            @click="closeDeleteModal"
            class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
          >
            Annulla
          </button>
          <button 
            type="button" 
            @click="deleteRegistry"
            class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md"
          >
            Elimina
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useForm, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageServerTable from '@/Components/PageServerTable.vue';
import 'vue-select/dist/vue-select.css';

export default {
  components: {
    BreadCrumbs,
    PageServerTable
  },
  props: {
    items: Array,
    count: Number,
    ippo: Array,
    customerBasedSchemaEnabled: {
      type: Boolean,
      default: false
    },
    customerFilter: {
      type: String,
      default: null
    },
    allCustomers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showModal: false,
      showDeleteModal: false,
      isEditing: false,
      editingRegistry: null,
      registryToDelete: null,
      headers: [],
      sortby: [],
      ipp: 10,
      page: 1,
      activeCustomerFilter: null,
      activeCustomerCode: null,
      isCustomerBasedSchemaEnabledLocal: false,
      selectedCustomer: null,
      form: useForm({
        name: '',
        description: ''
      }),
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Registri Personalizzati', href: route('modules.registri-personalizzati.index') }
      ]
    };
  },
  methods: {
    reload(sortby = undefined, itemsperpage = undefined, page = undefined) {
      if (sortby) this.sortby = sortby;
      if (itemsperpage) this.ipp = itemsperpage;
      if (page) this.page = page;

      router.post(window.location.href, {
        headers: this.headers,
        sortby: this.sortby,
        itemsperpage: this.ipp,
        page: this.page
      }, {
        only: ['items', 'count', 'customerBasedSchemaEnabled', 'customerFilter', 'allCustomers'],
        preserveScroll: true
      });
    },
    applyCustomerFilter() {
      if (!this.selectedCustomer) {
        this.clearCustomerFilter();
        return;
      }
      this.activeCustomerFilter = this.selectedCustomer.companyName;
      this.activeCustomerCode = this.selectedCustomer.custSupp;
      const url = route('modules.registri-personalizzati.index');
      router.visit(`${url}?customer=${encodeURIComponent(this.selectedCustomer.custSupp)}`, {
        only: ['items', 'count', 'customerFilter'],
        preserveState: true,
        preserveScroll: true
      });
    },
    clearCustomerFilter() {
      this.activeCustomerFilter = null;
      this.activeCustomerCode = null;
      this.selectedCustomer = null;
      router.visit(route('modules.registri-personalizzati.index'), {
        preserveState: true
      });
    },
    openCreateModal() {
      this.isEditing = false;
      this.editingRegistry = null;
      this.form.reset();
      this.form.clearErrors();
      this.showModal = true;
    },
    openEditModal(registry) {
      this.isEditing = true;
      this.editingRegistry = registry;
      this.form.name = registry.name;
      this.form.description = registry.description === '-' ? '' : registry.description;
      this.form.clearErrors();
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.form.reset();
      this.form.clearErrors();
    },
    confirmDelete(registry) {
      this.registryToDelete = registry;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.registryToDelete = null;
    },
    submitForm() {
      if (this.isEditing) {
        this.form.put(route('modules.registri-personalizzati.update', this.editingRegistry.id), {
          onSuccess: () => {
            this.closeModal();
            this.reload();
          }
        });
      } else {
        this.form.post(route('modules.registri-personalizzati.store'), {
          onSuccess: () => {
            this.closeModal();
            this.reload();
          }
        });
      }
    },
    deleteRegistry() {
      router.delete(route('modules.registri-personalizzati.destroy', this.registryToDelete.id), {
        onSuccess: () => {
          this.closeDeleteModal();
          this.reload();
        }
      });
    },
    openRegistry(itemOrEvent, rowOrCustomerFilter = null) {
      let registryId;
      let customerFilter = null;

      if (rowOrCustomerFilter && rowOrCustomerFilter.item) {
        registryId = rowOrCustomerFilter.item.id;
      } else if (itemOrEvent && itemOrEvent.id) {
        registryId = itemOrEvent.id;
        customerFilter = rowOrCustomerFilter;
      } else {
        return;
      }

      let url = route('modules.registri-personalizzati.show', registryId);
      const filter = customerFilter || this.activeCustomerCode;
      if (filter) {
        url += `?customer=${encodeURIComponent(filter)}`;
      }

      router.visit(url);
    }
  },
  mounted() {
    this.headers = this.$attrs.headers;
    this.ipp = this.$attrs.ipp;
    this.page = this.$attrs.page;
    this.sortby = this.$attrs.sortby;
    
    this.isCustomerBasedSchemaEnabledLocal = !!this.customerBasedSchemaEnabled;

    if (this.customerFilter) {
      this.activeCustomerCode = this.customerFilter;
      const found = this.allCustomers.find(c => c.custSupp === this.customerFilter);
      this.activeCustomerFilter = found ? found.companyName : this.customerFilter;
      this.selectedCustomer = found || { custSupp: this.customerFilter, companyName: this.customerFilter };
    }
  },
  watch: {
    customerBasedSchemaEnabled(newVal) {
      this.isCustomerBasedSchemaEnabledLocal = !!newVal;
    },
    selectedCustomer(newVal) {
      if (newVal) {
        this.applyCustomerFilter();
      }
    }
  }
};
</script>
